
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";

interface PeppolScheme {
	code: string;
	label: string;
}

const PEPPOL_SCHEMES: PeppolScheme[] = [
	{ code: "0208", label: "BE Ondernemingsnummer" },
	{ code: "9925", label: "BE BTW-nummer" },
	{ code: "0088", label: "GLN (EAN)" },
	{ code: "9944", label: "NL BTW-nummer" },
	{ code: "0106", label: "NL KvK-nummer" },
	{ code: "9930", label: "DE BTW-nummer" },
	{ code: "9957", label: "FR BTW-nummer" },
	{ code: "9938", label: "LU BTW-nummer" },
	{ code: "0190", label: "NL OIN" },
	{ code: "0060", label: "DUNS" },
	{ code: "0199", label: "LEI" },
];

export default Vue.extend({
	data() {
		return {
			selectedScheme: "" as string,
			endpointNumber: "" as string,
			isUpdatingFromProp: false
		};
	},
	props: {
		value: {
			type: String,
			default: ""
		}
	},
	computed: {
		schemeItems(): ISelectItem<string>[] {
			const items: ISelectItem<string>[] = [
				{ value: "", text: this.$ct("geen-schema") as string }
			];
			for (const scheme of PEPPOL_SCHEMES) {
				items.push({
					value: scheme.code,
					text: `${scheme.code} - ${scheme.label}`
				});
			}
			return items;
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(newVal: string) {
				if (this.isUpdatingFromProp) return;
				this.parseValue(newVal);
			}
		}
	},
	methods: {
		parseValue(val: string) {
			if (!val) {
				this.selectedScheme = "";
				this.endpointNumber = "";
				return;
			}

			// Check if value contains a known scheme prefix (e.g., "0088:123456")
			const colonIndex = val.indexOf(":");
			if (colonIndex > 0) {
				const possibleScheme = val.substring(0, colonIndex);
				const knownScheme = PEPPOL_SCHEMES.find(s => s.code === possibleScheme);
				if (knownScheme) {
					this.selectedScheme = possibleScheme;
					this.endpointNumber = val.substring(colonIndex + 1);
					return;
				}
			}

			// No known scheme found, just put everything in the number field
			this.selectedScheme = "";
			this.endpointNumber = val;
		},
		buildFullValue(): string {
			if (!this.endpointNumber) {
				return "";
			}
			if (this.selectedScheme) {
				return `${this.selectedScheme}:${this.endpointNumber}`;
			}
			return this.endpointNumber;
		},
		emitValue() {
			this.isUpdatingFromProp = true;
			this.$emit("input", this.buildFullValue());
			this.$nextTick(() => {
				this.isUpdatingFromProp = false;
			});
		},
		cleanNumber(num: string): string {
			// Remove spaces, dots, dashes and other common separators
			return num.replace(/[\s.\-\/]/g, "").toUpperCase();
		},
		detectSchemeFromNumber(num: string): string | null {
			const cleaned = this.cleanNumber(num);

			// BE BTW-nummer: starts with BE followed by 10 digits (e.g., BE0123456789)
			if (/^BE\d{10}$/.test(cleaned)) {
				return "9925";
			}

			// BE Ondernemingsnummer: 10 digits starting with 0 or 1 (e.g., 0123456789)
			if (/^[01]\d{9}$/.test(cleaned)) {
				return "0208";
			}

			// NL BTW-nummer: starts with NL, 9 digits, B, 2 chars (e.g., NL123456789B01)
			if (/^NL\d{9}B\d{2}$/.test(cleaned)) {
				return "9944";
			}

			// GLN: exactly 13 digits
			if (/^\d{13}$/.test(cleaned)) {
				return "0088";
			}

			// DE BTW-nummer: starts with DE followed by 9 digits
			if (/^DE\d{9}$/.test(cleaned)) {
				return "9930";
			}

			// FR BTW-nummer: starts with FR, 2 chars (key), 9 digits (e.g., FRXX999999999)
			if (/^FR[A-Z0-9]{2}\d{9}$/.test(cleaned)) {
				return "9957";
			}

			// LU BTW-nummer: starts with LU followed by 8 digits
			if (/^LU\d{8}$/.test(cleaned)) {
				return "9938";
			}

			return null;
		},
		onSchemeChange(newScheme: string) {
			this.adjustNumberForScheme(newScheme);
			this.emitValue();
			this.$emit("blur");
		},
		adjustNumberForScheme(scheme: string) {
			if (!this.endpointNumber) return;

			const num = this.endpointNumber;

			if (scheme === "0208") {
				// BE Ondernemingsnummer: strip BE prefix if present
				if (num.startsWith("BE") && /^BE\d{10}$/.test(num)) {
					this.endpointNumber = num.substring(2);
				}
			} else if (scheme === "9925") {
				// BE BTW-nummer: add BE prefix if missing
				if (/^\d{10}$/.test(num) && !num.startsWith("BE")) {
					this.endpointNumber = "BE" + num;
				}
			} else if (scheme === "9944") {
				// NL BTW-nummer: add NL prefix if missing
				if (/^\d{9}B\d{2}$/.test(num) && !num.startsWith("NL")) {
					this.endpointNumber = "NL" + num;
				}
			} else if (scheme === "9930") {
				// DE BTW-nummer: add DE prefix if missing
				if (/^\d{9}$/.test(num) && !num.startsWith("DE")) {
					this.endpointNumber = "DE" + num;
				}
			} else if (scheme === "9957") {
				// FR BTW-nummer: add FR prefix if missing
				if (/^[A-Z0-9]{2}\d{9}$/.test(num) && !num.startsWith("FR")) {
					this.endpointNumber = "FR" + num;
				}
			} else if (scheme === "9938") {
				// LU BTW-nummer: add LU prefix if missing
				if (/^\d{8}$/.test(num) && !num.startsWith("LU")) {
					this.endpointNumber = "LU" + num;
				}
			}
		},
		onNumberChange() {
			this.emitValue();
		},
		onNumberBlur() {
			const cleaned = this.cleanNumber(this.endpointNumber);
			if (cleaned !== this.endpointNumber) {
				this.endpointNumber = cleaned;
				this.emitValue();
			}
			this.$emit("blur");
		},
		onPaste(event: ClipboardEvent) {
			const pastedText = event.clipboardData?.getData("text")?.trim();
			if (!pastedText) return;

			// Check if pasted text contains a scheme prefix (e.g., "0088:123456")
			const colonIndex = pastedText.indexOf(":");
			if (colonIndex > 0) {
				const possibleScheme = pastedText.substring(0, colonIndex);
				const knownScheme = PEPPOL_SCHEMES.find(s => s.code === possibleScheme);
				if (knownScheme) {
					event.preventDefault();
					this.selectedScheme = possibleScheme;
					this.endpointNumber = this.cleanNumber(pastedText.substring(colonIndex + 1));
					this.emitValue();
					this.$emit("blur");
					return;
				}
			}

			// No explicit schema, try to detect from number format
			event.preventDefault();
			const cleanedNumber = this.cleanNumber(pastedText);
			const detectedScheme = this.detectSchemeFromNumber(cleanedNumber);
			if (detectedScheme) {
				this.selectedScheme = detectedScheme;
			}
			this.endpointNumber = cleanedNumber;
			this.emitValue();
			this.$emit("blur");
		}
	},
	i18nEx: {
		componentPrefix: "components.peppol"
	}
});
